import { formatDistanceToNow } from "date-fns";
import type {
  CorpusFeed,
  CorpusFeedOpenRunProgress,
  CorpusFeedRunResult,
  CorpusFeedStatistics,
  CorpusFeedState,
} from "@shared/types/corpus/api.ts";
import { splitLeadingEmoji } from "./text";

const STATE_LABELS: Record<CorpusFeedState, string> = {
  active: "Active",
  paused: "Paused",
  retired: "Retired",
};

/** What became of a feed's papers, in the order they are shown. */
export type FeedOutcomeKey =
  "ingested" | "needs_review" | "rejected" | "existing" | "failed" | "open";

/** One kind of end to a feed's papers, and how many met it. */
export interface FeedOutcomeCount {
  key: FeedOutcomeKey;
  label: string;
  count: number;
}

/** The papers a feed returned, grouped by what became of them; a kind with no papers is left out. */
export function feedOutcomeCounts(statistics: CorpusFeedStatistics): FeedOutcomeCount[] {
  const { byOutcome } = statistics;
  const counts: FeedOutcomeCount[] = [
    { key: "ingested", label: "Ingested", count: byOutcome.ingested },
    { key: "needs_review", label: "Needs review", count: byOutcome.needs_review },
    {
      key: "rejected",
      label: "Rejected",
      count: byOutcome.rejected + byOutcome.previously_rejected,
    },
    { key: "existing", label: "Already in corpus", count: byOutcome.existing },
    { key: "failed", label: "Failed", count: statistics.failedCount },
    { key: "open", label: "Still to process", count: statistics.openCount },
  ];
  return counts.filter((outcome) => outcome.count > 0);
}

/** What starting a run did, in a sentence for the person who asked for it. */
export function describeCorpusFeedRun(result: CorpusFeedRunResult): string {
  switch (result.outcome) {
    case "already_running":
      return "A run is already in progress.";
    case "nothing_new":
      return "Nothing new since the last run.";
    case "started": {
      const papers = result.job?.itemCount ?? 0;
      const started = `Started a run of ${papers.toLocaleString()} new ${papers === 1 ? "paper" : "papers"}.`;
      return result.deferredCount > 0
        ? `${started} ${result.deferredCount.toLocaleString()} more will come with the next run.`
        : started;
    }
  }
}

function timeFromNow(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

/**
 * Reads a corpus feed, and what it has brought in once that is known, the way a person sees it, so
 * the wording of its state, runs and numbers lives here and not in the components that show them.
 */
export class CorpusFeedView {
  constructor(
    readonly feed: CorpusFeed,
    readonly statistics?: CorpusFeedStatistics
  ) {}

  /** The feed's name as shown, without the emoji it may start with. */
  get name(): string {
    return splitLeadingEmoji(this.feed.name).rest;
  }

  /** The emoji the feed's name starts with, which stands for the feed; null when it has none. */
  get icon(): string | null {
    return splitLeadingEmoji(this.feed.name).emoji;
  }

  get stateLabel(): string {
    return STATE_LABELS[this.feed.state];
  }

  /** A feed that is not running, paused or retired. */
  get isQuiet(): boolean {
    return this.feed.state !== "active";
  }

  /** How far the feed's open run has got; null when none is open or the statistics are not known. */
  get openRun(): CorpusFeedOpenRunProgress | null {
    return this.statistics?.openRun ?? null;
  }

  /** The papers returned, by what became of them. */
  get outcomes(): FeedOutcomeCount[] {
    return this.statistics ? feedOutcomeCounts(this.statistics) : [];
  }

  /** What the last run was, or how far the one in progress is. */
  get lastRunText(): string {
    const run = this.openRun;
    if (run) {
      return `Running · ${run.processedCount.toLocaleString()} of ${run.totalCount.toLocaleString()}`;
    }
    return this.feed.lastRunAt ? timeFromNow(this.feed.lastRunAt) : "Never";
  }

  /** When the schedule runs the feed next. A feed never run has no date, and one whose date has
   *  passed is overdue (a run left papers behind): both are run at the next hourly check. */
  get nextRunText(): string {
    if (this.feed.state === "paused") return "Paused";
    if (this.feed.state === "retired") return "Not scheduled";
    if (this.feed.nextRunAt === null || new Date(this.feed.nextRunAt) <= new Date()) {
      return "At the next check";
    }
    return timeFromNow(this.feed.nextRunAt);
  }

  /** Papers the feed returned, whatever became of them; null while the statistics are not known. */
  get returnedText(): string | null {
    return this.statistics ? this.statistics.returnedCount.toLocaleString() : null;
  }

  /** Papers the feed ingested; null while the statistics are not known. */
  get ingestedText(): string | null {
    return this.statistics ? this.statistics.byOutcome.ingested.toLocaleString() : null;
  }

  /** Share of processed papers the admission gate rejected, as a percentage; a dash before any
   *  paper was processed, and null while the statistics are not known. */
  get rejectionRateText(): string | null {
    if (!this.statistics) return null;
    const rate = this.statistics.rejectionRate;
    return rate === null ? "—" : `${Math.round(rate * 100)}%`;
  }
}
